// PrimaryGpsView — The default "GPS-style" trip view: a single dominant
// surface showing where the traveler is right now and what to do next,
// instead of the full always-expanded itinerary (TimelineView).
//
// This is a presentational component — all selection logic (which stage is
// active, what the next action is) lives in journeyStageEngine and
// nextActionEngine, exactly as it does for the full timeline. This
// component's job is purely to give that already-computed answer a
// full-surface, dominant visual treatment instead of a slim sticky banner,
// and to host the (Phase 3) dining-moment card in the idle state.
//
// Reuses NextActionBanner unchanged for the instruction/action-button/idle
// content — its accessibility behavior (role="status", aria-live="polite")
// and existing tests stay valid regardless of the surrounding chrome.

import React from 'react';
import {
  Building,
  Car,
  CheckCircle,
  Clock,
  Coffee,
  MapPin,
  Navigation,
  Plane,
  PlaneLanding,
  Shield,
  List,
} from 'lucide-react';
import { colors, radii, typography, fontWeights, shadows } from '../../styles/designTokens';
import { STAGES, STAGE_LABELS } from '../../services/journeyStageEngine';
import { NextActionBanner } from './NextActionBanner';

// Mirrors the STAGE_ICONS map in buildJourneyEvents.js so all journey
// surfaces draw from the same visual vocabulary (see that file's comment).
const STAGE_ICONS = {
  [STAGES.PRE_TRIP]: Clock,
  [STAGES.DEPARTURE_FROM_ORIGIN]: Car,
  [STAGES.AIRPORT_ENTRY]: Building,
  [STAGES.SECURITY_CHECKIN]: Shield,
  [STAGES.NAVIGATE_TO_GATE]: Navigation,
  [STAGES.WAITING_BOARDING]: Coffee,
  [STAGES.IN_FLIGHT]: Plane,
  [STAGES.ARRIVAL_NAVIGATION]: PlaneLanding,
  [STAGES.GROUND_TRANSPORT]: Car,
  [STAGES.AT_DESTINATION]: MapPin,
  [STAGES.TRIP_COMPLETE]: CheckCircle,
};

// Stages where a progress bar is meaningful — PRE_TRIP/TRIP_COMPLETE and the
// open-ended AT_DESTINATION window read as noise, not signal, as a bar.
const PROGRESS_STAGES = new Set([
  STAGES.DEPARTURE_FROM_ORIGIN,
  STAGES.AIRPORT_ENTRY,
  STAGES.SECURITY_CHECKIN,
  STAGES.NAVIGATE_TO_GATE,
  STAGES.WAITING_BOARDING,
  STAGES.IN_FLIGHT,
  STAGES.ARRIVAL_NAVIGATION,
  STAGES.GROUND_TRANSPORT,
]);

const containerStyle = {
  backgroundColor: colors.surface,
  borderRadius: radii.card,
  boxShadow: shadows.card,
  overflow: 'hidden',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '20px 20px 16px',
};

const iconCircleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  backgroundColor: colors.infoBackground,
  color: colors.infoText,
  flexShrink: 0,
};

const stageLabelStyle = {
  fontSize: typography.heading.fontSize,
  fontWeight: fontWeights.bold,
  color: colors.textPrimary,
  margin: 0,
};

const progressTrackStyle = {
  marginTop: '8px',
  height: '4px',
  borderRadius: radii.pill,
  backgroundColor: colors.divider,
  overflow: 'hidden',
};

const itineraryLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: 'auto',
  padding: '6px 10px',
  borderRadius: radii.inner,
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.medium,
  color: colors.textSecondary,
  backgroundColor: 'transparent',
  border: `1px solid ${colors.divider}`,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minHeight: '32px',
};

/**
 * @param {Object} props
 * @param {Object|null} props.activeStage - `resolveTripStages(...).activeStage`
 * @param {Object} props.nextAction - `computeNextAction(...)` result
 * @param {string|null} [props.departureTimeDisplay]
 * @param {() => void} props.onOpenItinerary - Switches TripCardFull's viewMode to 'itinerary'
 * @param {React.ReactNode} [props.children] - Slot for Phase 3's DiningMomentCard, rendered
 *   below the hero only when the parent decides the idle state applies.
 */
export function PrimaryGpsView({ activeStage, nextAction, departureTimeDisplay, onOpenItinerary, children }) {
  const stage = activeStage?.stage;
  const label = (stage && STAGE_LABELS[stage]) || 'Your Trip';
  const Icon = (stage && STAGE_ICONS[stage]) || MapPin;
  const showProgress = stage && PROGRESS_STAGES.has(stage);
  const progress = showProgress ? Math.round((activeStage.progress || 0) * 100) : null;

  return (
    <div data-testid="primary-gps-view" style={containerStyle}>
      <div style={headerRowStyle}>
        <div style={iconCircleStyle} aria-hidden="true">
          <Icon size={26} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p data-testid="gps-stage-label" style={stageLabelStyle}>{label}</p>
          {showProgress && (
            <div
              style={progressTrackStyle}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${label} progress`}
            >
              <div
                data-testid="gps-progress-fill"
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: colors.info,
                  borderRadius: radii.pill,
                  transition: 'width 0.3s ease-out',
                }}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid="open-itinerary-link"
          onClick={onOpenItinerary}
          style={itineraryLinkStyle}
          aria-label="View full itinerary"
        >
          <List size={14} aria-hidden="true" />
          Full itinerary
        </button>
      </div>

      <NextActionBanner
        instruction={nextAction.instruction}
        actionButton={nextAction.actionButton}
        isIdle={nextAction.isIdle}
        idleMessage={nextAction.idleMessage}
        departureTime={stage === STAGES.DEPARTURE_FROM_ORIGIN ? departureTimeDisplay : undefined}
      />

      {children && (
        <div style={{ padding: '0 20px 20px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default PrimaryGpsView;
